/**
 * 可释放的 Three.js 资源最小契约。
 *
 * 说明：这里不绑定具体 THREE 类型，避免工具函数反向依赖渲染对象层级。
 */
export interface DisposableResource {
  /** 释放底层 GPU 或运行时资源。 */
  dispose(): void;
}

/**
 * 已排队等待释放的资源集合。
 *
 * 说明：同一个几何体或材质在多条清理链路中可能被重复触达，WeakSet 可避免重复 dispose，
 * 同时不阻止资源被垃圾回收。
 */
const scheduledDisposableResources = new WeakSet<DisposableResource>();

/**
 * 在当前渲染帧结束后释放单个资源。
 *
 * 设计原因：
 * 1. WebGPU/WebGL 渲染器可能仍在当前帧引用刚被移除的 BufferGeometry 或 Material；
 * 2. 立即 dispose 会导致当前帧访问已销毁缓冲，进而出现渲染错误或不稳定闪烁；
 * 3. 使用 requestAnimationFrame + setTimeout 将释放推迟到下一帧之后，兼顾浏览器渲染安全与测试可控性。
 *
 * @param resource 待释放资源；为空时忽略。
 */
export function scheduleDisposableResourceDisposal(resource: DisposableResource | null | undefined): void {
  if (!resource || scheduledDisposableResources.has(resource)) {
    return;
  }

  scheduledDisposableResources.add(resource);
  scheduleAfterRenderFrame(() => {
    scheduledDisposableResources.delete(resource);
    resource.dispose();
  });
}

/**
 * 在当前渲染帧结束后批量释放资源。
 *
 * @param resources 待释放资源列表。
 */
export function scheduleDisposableResourcesDisposal(
  resources: ReadonlyArray<DisposableResource | null | undefined>,
): void {
  resources.forEach(scheduleDisposableResourceDisposal);
}

/**
 * 将回调安排到当前渲染帧之后执行。
 *
 * @param callback 需要延迟执行的释放回调。
 */
function scheduleAfterRenderFrame(callback: () => void): void {
  if (typeof globalThis.requestAnimationFrame === 'function') {
    globalThis.requestAnimationFrame(() => {
      scheduleMacrotask(callback);
    });
    return;
  }

  scheduleMacrotask(callback);
}

/**
 * 将回调推入宏任务队列。
 *
 * @param callback 需要延迟执行的释放回调。
 */
function scheduleMacrotask(callback: () => void): void {
  if (typeof globalThis.setTimeout === 'function') {
    globalThis.setTimeout(callback, 0);
    return;
  }

  if (typeof globalThis.queueMicrotask === 'function') {
    globalThis.queueMicrotask(callback);
    return;
  }

  callback();
}
